<template>
  <div class="report-issue-page">
    <div class="page-header">
      <h1 class="title">🚨 Report Issue</h1>
      <p class="subtitle">Report bugs and issues to the on-call CTO function</p>
    </div>

    <!-- Alert Box -->
    <b-message
      v-if="alert.show"
      :type="alert.type"
      has-icon
      :closable="true"
      @close="alert.show = false"
    >
      {{ alert.message }}
    </b-message>

    <!-- Quick Templates -->
    <div class="box">
      <h2 class="subtitle">Quick Templates</h2>
      <div class="buttons">
        <b-button size="is-small" @click="applyTemplate('feature')">
          Feature Not Working
        </b-button>
        <b-button size="is-small" @click="applyTemplate('data')">
          Data Issue
        </b-button>
        <b-button size="is-small" @click="applyTemplate('performance')">
          Performance Problem
        </b-button>
        <b-button size="is-small" @click="applyTemplate('user')">
          User Complaint
        </b-button>
      </div>
    </div>

    <!-- Issue Report Form -->
    <div class="box">
      <form @submit.prevent="submitReport">
        <b-field label="Severity *" label-position="on-border">
          <b-select
            v-model="form.severity"
            placeholder="Select severity"
            required
            expanded
          >
            <option value="critical">🔴 Critical - System Down</option>
            <option value="high">🟠 High - Major Feature Broken</option>
            <option value="medium">🟡 Medium - Minor Issue</option>
            <option value="low">🟢 Low - Cosmetic/Enhancement</option>
          </b-select>
        </b-field>

        <b-field label="Category *" label-position="on-border">
          <b-select
            v-model="form.category"
            placeholder="Select category"
            required
            expanded
          >
            <option value="bug">Bug - Something broken</option>
            <option value="performance">Performance - Slow/timeout</option>
            <option value="data_issue">Data Issue - Wrong/missing data</option>
            <option value="user_complaint"
              >User Complaint - User reported</option
            >
            <option value="other">Other</option>
          </b-select>
        </b-field>

        <b-field label="Issue Title *" label-position="on-border">
          <b-input
            v-model="form.title"
            placeholder="Short, descriptive title"
            required
            maxlength="200"
          />
        </b-field>

        <b-field label="Affected Feature" label-position="on-border">
          <b-input
            v-model="form.affected_feature"
            placeholder="e.g., Episode Management, Schedule Export"
            maxlength="100"
          />
        </b-field>

        <b-field label="Description *" label-position="on-border">
          <b-input
            v-model="form.description"
            type="textarea"
            placeholder="Detailed explanation of the issue"
            required
            rows="6"
          />
        </b-field>

        <b-field label="Steps to Reproduce" label-position="on-border">
          <b-input
            v-model="form.steps_to_reproduce"
            type="textarea"
            placeholder="1. Go to...&#10;2. Click on...&#10;3. Notice that..."
            rows="4"
          />
        </b-field>

        <b-field label="Expected Behavior" label-position="on-border">
          <b-input
            v-model="form.expected_behavior"
            type="textarea"
            placeholder="What should happen"
            rows="3"
          />
        </b-field>

        <b-field label="Actual Behavior" label-position="on-border">
          <b-input
            v-model="form.actual_behavior"
            type="textarea"
            placeholder="What actually happens"
            rows="3"
          />
        </b-field>

        <div class="columns">
          <div class="column">
            <b-field label="Related User ID" label-position="on-border">
              <b-input
                v-model="form.related_user_id"
                placeholder="If a specific user is affected"
                type="number"
              />
            </b-field>
          </div>
          <div class="column">
            <b-field
              label="Related Record/Episode ID"
              label-position="on-border"
            >
              <b-input
                v-model="form.related_record_id"
                placeholder="Episode, media, or schedule ID"
                type="number"
              />
            </b-field>
          </div>
        </div>

        <div class="buttons">
          <b-button
            type="is-primary"
            native-type="submit"
            :loading="isSubmitting"
            icon-left="send"
          >
            Submit Issue Report
          </b-button>
          <b-button :disabled="isSubmitting" @click="resetForm">
            Clear Form
          </b-button>
        </div>
      </form>
    </div>

    <!-- Recent Reports -->
    <div v-if="recentReports.length > 0" class="box">
      <h2 class="subtitle">Recent Reports (Last 5)</h2>
      <div class="report-list">
        <div
          v-for="report in recentReports"
          :key="report.id"
          class="report-item"
        >
          <div class="report-header">
            <span :class="['severity-badge', `severity-${report.severity}`]">
              {{ report.severity.toUpperCase() }}
            </span>
            <span class="report-date">{{ formatDate(report.created_at) }}</span>
          </div>
          <h3 class="report-title">{{ report.title }}</h3>
          <p class="report-description">
            {{ truncate(report.description, 150) }}
          </p>
          <div class="report-meta">
            <span class="tag">{{ report.category }}</span>
            <span class="tag is-light">{{ report.status }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminReportsApi from '@/api/adminReports'
import { format } from 'date-fns'

export default {
  name: 'ReportIssue',
  data() {
    return {
      form: {
        severity: '',
        category: '',
        title: '',
        affected_feature: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        related_user_id: null,
        related_record_id: null
      },
      alert: {
        show: false,
        type: 'is-success',
        message: ''
      },
      isSubmitting: false,
      recentReports: []
    }
  },
  mounted() {
    this.loadRecentReports()
  },
  methods: {
    async submitReport() {
      this.isSubmitting = true

      try {
        const payload = {
          ...this.form,
          related_table_name: this.form.related_record_id ? 'episodes' : null
        }

        await AdminReportsApi.create(payload)

        this.showAlert(
          '✅ Issue report submitted successfully! On-call CTO will investigate.',
          'is-success'
        )
        this.resetForm()
        this.loadRecentReports()

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (error) {
        console.error('Error submitting report:', error)
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          'Failed to submit report'
        this.showAlert(`❌ Error: ${errorMessage}`, 'is-danger')
      } finally {
        this.isSubmitting = false
      }
    },

    async loadRecentReports() {
      try {
        const reports = await AdminReportsApi.getAll()
        this.recentReports = reports.slice(0, 5)
      } catch (error) {
        console.error('Error loading recent reports:', error)
      }
    },

    resetForm() {
      this.form = {
        severity: '',
        category: '',
        title: '',
        affected_feature: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        related_user_id: null,
        related_record_id: null
      }
    },

    applyTemplate(type) {
      const templates = {
        feature: {
          severity: 'high',
          category: 'bug',
          title: 'Feature not working as expected',
          description:
            'Describe what feature is not working and what you expected to happen.',
          steps_to_reproduce: '1. Go to...\n2. Click on...\n3. Notice that...',
          expected_behavior: 'The feature should...',
          actual_behavior: 'Instead, the feature...'
        },
        data: {
          severity: 'medium',
          category: 'data_issue',
          title: 'Data issue - incorrect or missing data',
          description: 'Describe what data is incorrect or missing.',
          steps_to_reproduce:
            '1. View...\n2. Check field...\n3. Notice incorrect value...',
          expected_behavior: 'The data should show...',
          actual_behavior: 'The data shows...'
        },
        performance: {
          severity: 'medium',
          category: 'performance',
          title: 'Performance issue - slow or timeout',
          description: 'Describe what operation is slow or timing out.',
          steps_to_reproduce:
            '1. Navigate to...\n2. Perform action...\n3. Notice slow response...',
          expected_behavior: 'The operation should complete quickly',
          actual_behavior: 'The operation takes too long or times out'
        },
        user: {
          severity: 'medium',
          category: 'user_complaint',
          title: 'User reported issue',
          description: 'User complained about...',
          steps_to_reproduce: 'User reported:\n1. ...\n2. ...',
          expected_behavior: 'User expected...',
          actual_behavior: 'User experienced...'
        }
      }

      const template = templates[type]
      if (template) {
        Object.assign(this.form, template)
      }
    },

    showAlert(message, type) {
      this.alert = {
        show: true,
        type,
        message
      }

      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },

    formatDate(dateString) {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    },

    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
  }
}
</script>

<style scoped>
.report-issue-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header .title {
  font-size: 32px;
  color: #363636;
  margin-bottom: 8px;
}

.page-header .subtitle {
  font-size: 16px;
  color: #7a7a7a;
}

.box {
  margin-bottom: 20px;
}

.report-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.report-item {
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.severity-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.severity-critical {
  background: #ff6b6b;
  color: white;
}

.severity-high {
  background: #ffa94d;
  color: white;
}

.severity-medium {
  background: #ffd93d;
  color: #333;
}

.severity-low {
  background: #51cf66;
  color: white;
}

.report-date {
  font-size: 13px;
  color: #999;
}

.report-title {
  font-size: 16px;
  font-weight: 600;
  color: #363636;
  margin-bottom: 8px;
}

.report-description {
  font-size: 14px;
  color: #7a7a7a;
  margin-bottom: 10px;
  line-height: 1.5;
}

.report-meta {
  display: flex;
  gap: 8px;
}
</style>
